import { useState } from "react";
import { Clock, MapPin, CheckCircle2, Plus } from "lucide-react";
import { SERVICES, ADDONS } from "@/lib/data";
import { BookingModal } from "@/components/BookingModal";

const HEADER_IMG =
  "https://customer-assets.emergentagent.com/job_wellness-at-home-15/artifacts/ay73xzg8_FB_IMG_1780415792482.jpg";

export default function Services() {
  const [bookingFor, setBookingFor] = useState(null);

  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="relative">
        <div className="container-spa pt-16 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-5">Mobile · In-home · Knoxville</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-spa-dark leading-[1.02]">
                Services & Pricing
              </h1>
              <p className="mt-7 text-lg text-spa-gray max-w-xl leading-relaxed">
                Every session is delivered in your home, fully set up by your therapist — heated table,
                fresh linens, warm oils, and a quiet playlist.
              </p>
              <div className="mt-8 flex flex-wrap gap-6 text-sm text-spa-gray">
                <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-spa-gold" strokeWidth={1.5} /> 25-mile service radius</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-spa-gold" strokeWidth={1.5} /> 60 & 90 minute options</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl aspect-[5/4]">
                <img src={HEADER_IMG} alt="Tranquil massage scene" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="bg-spa-cream">
        <div className="container-spa pb-24">
          <div className="space-y-6">
            {SERVICES.map((s, i) => (
              <div
                key={s.id}
                data-testid={`service-row-${s.id}`}
                className="card-spa flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="lg:w-2/5">
                  <p className="overline text-spa-gold mb-3">{`0${i + 1}`}</p>
                  <h3 className="font-serif text-3xl text-spa-dark mb-3">{s.name}</h3>
                  <p className="text-spa-gray leading-relaxed">{s.description}</p>
                </div>

                <div className="lg:w-2/5 space-y-3">
                  {s.durations.map((d) => (
                    <div key={d.label} className="bg-spa-sand rounded-xl p-5 flex items-baseline justify-between">
                      <span className="font-sans text-sm text-spa-gray">{d.label}</span>
                      <span className="font-serif text-3xl text-spa-dark">${d.price}</span>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-2">
                    {s.benefits.map((b) => (
                      <span key={b} className="text-xs px-3 py-1 rounded-full bg-white border border-spa-border text-spa-gray inline-flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-spa-green" strokeWidth={1.5} /> {b}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="lg:w-1/5 lg:text-right">
                  <button
                    data-testid={`book-button-${s.id}`}
                    onClick={() => setBookingFor(s)}
                    className="btn-primary w-full lg:w-auto"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 card-spa">
            <div className="flex items-center gap-3 mb-6">
              <Plus className="w-5 h-5 text-spa-cyan" strokeWidth={1.5} />
              <p className="overline">Add-Ons</p>
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl text-spa-dark mb-8">Make it yours.</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {ADDONS.map((a) => (
                <div key={a.id} data-testid={`addon-${a.id}`} className="bg-spa-sand rounded-xl p-5 flex items-baseline justify-between">
                  <span className="font-sans text-sm text-spa-dark">{a.name}</span>
                  <span className="font-serif text-2xl text-spa-dark whitespace-nowrap">+${a.price}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-spa-gray mt-6">Select any add-ons during booking — the total adjusts automatically and we&apos;ll prep everything in advance.</p>
          </div>

          {/* TRAVEL & SERVICE AREAS */}
          <div className="mt-8 card-spa" data-testid="travel-section">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-5 h-5 text-spa-cyan" strokeWidth={1.5} />
              <p className="overline">Travel & Service Areas</p>
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl text-spa-dark mb-4">We come to you.</h3>
            <p className="text-spa-gray leading-relaxed max-w-2xl mb-8">
              We bring the full spa experience directly to your door across East Tennessee.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div
                data-testid="travel-zone-1"
                className="relative bg-spa-sand rounded-2xl p-6 border border-spa-border overflow-hidden"
              >
                <span className="overline text-spa-gold">Zone 1</span>
                <h4 className="font-serif text-2xl text-spa-dark mt-2 mb-1">
                  Knoxville & Jefferson City
                </h4>
                <p className="text-sm text-spa-gray mb-5">Approximately 25-mile radius</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-4xl text-spa-dark">$20</span>
                  <span className="text-sm text-spa-gray">travel fee</span>
                </div>
              </div>

              <div
                data-testid="travel-zone-2"
                className="relative bg-spa-sand rounded-2xl p-6 border border-spa-border overflow-hidden"
              >
                <span className="overline text-spa-gold">Zone 2</span>
                <h4 className="font-serif text-2xl text-spa-dark mt-2 mb-1">
                  Maryville, Farragut & Sevierville
                </h4>
                <p className="text-sm text-spa-gray mb-5">Extended service area</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-4xl text-spa-dark">$40</span>
                  <span className="text-sm text-spa-gray">travel fee</span>
                </div>
              </div>
            </div>

            {/* Waiver perk callout */}
            <div
              data-testid="travel-waiver"
              className="mt-6 rounded-2xl bg-spa-green text-spa-cream p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5 border border-spa-green relative overflow-hidden"
            >
              <div className="shrink-0 w-12 h-12 rounded-full bg-spa-gold/20 border border-spa-gold/40 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-spa-gold" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="overline text-spa-gold mb-1">Travel Fee Waived</p>
                <p className="font-serif text-xl sm:text-2xl leading-snug">
                  Book 2 or more back-to-back sessions at the same location — couples,
                  groups, or neighbors — and your travel fee is <span className="text-spa-gold font-semibold">WAIVED</span>.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 card-spa bg-spa-green text-spa-cream border-spa-green text-center">
            <p className="overline text-spa-gold mb-4">Have something else in mind?</p>
            <h3 className="font-serif text-3xl sm:text-4xl mb-4">Couples, events & corporate</h3>
            <p className="text-spa-cream/70 max-w-xl mx-auto leading-relaxed mb-8">
              We offer in-home couples sessions, bridal packages, and on-site corporate wellness days.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                data-testid="services-custom-email"
                href="mailto:masonmassage865@gmail.com?subject=Custom%20package%20inquiry&body=Hi%20Stephanie%2C%20I'd%20like%20to%20learn%20more%20about%20a%20custom%20package%20(couples%2Fevent%2Fcorporate).%20Here%20are%20the%20details%3A%0A%0A"
                className="btn-accent"
              >
                Email Stephanie
              </a>
              <a
                data-testid="services-custom-call"
                href="tel:+18652217543"
                className="bg-transparent border border-spa-cream/40 text-spa-cream px-8 py-3.5 rounded-full font-medium hover:bg-spa-cream/10 transition-all inline-flex items-center justify-center"
              >
                Call (865) 221-7543
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING MODAL */}
      {bookingFor && <BookingModal service={bookingFor} onClose={() => setBookingFor(null)} />}
    </div>
  );
}
